// ============================================================================
// LOCATION UTILITIES — Normalized teacher location foundation
// ============================================================================
// Provides a single canonical shape for teacher locations across the app:
//   - Storage in teachers/{id}            → full NormalizedLocation (with lat/lng)
//   - Storage in publicTeacherProfiles    → public-safe subset (no precise coords)
//   - Future proximity queries            → uses geohash + lat/lng (private)
//
// Geocoding provider: Mapbox Geocoding API (forward geocoding).
// Token is read from import.meta.env.VITE_MAPBOX_TOKEN.
// If no token is configured, the autocomplete component falls back to a
// plain text input and locations are stored as free-text only.
// ============================================================================

// Inline geohash implementation (geohash precision 9, ~5m).
// Avoids adding a runtime dependency on geofire-common.
const GEOHASH_BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz'
function geohashForLocation(coords: [number, number], precision = 9): string {
  const [lat, lng] = coords
  if (
    typeof lat !== 'number' ||
    typeof lng !== 'number' ||
    Number.isNaN(lat) ||
    Number.isNaN(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    throw new Error('Invalid coordinates')
  }
  let latRange = [-90, 90]
  let lngRange = [-180, 180]
  let hash = ''
  let bits = 0
  let bitCount = 0
  let even = true
  while (hash.length < precision) {
    if (even) {
      const mid = (lngRange[0] + lngRange[1]) / 2
      if (lng >= mid) {
        bits = (bits << 1) | 1
        lngRange[0] = mid
      } else {
        bits = bits << 1
        lngRange[1] = mid
      }
    } else {
      const mid = (latRange[0] + latRange[1]) / 2
      if (lat >= mid) {
        bits = (bits << 1) | 1
        latRange[0] = mid
      } else {
        bits = bits << 1
        latRange[1] = mid
      }
    }
    even = !even
    bitCount++
    if (bitCount === 5) {
      hash += GEOHASH_BASE32[bits]
      bits = 0
      bitCount = 0
    }
  }
  return hash
}

/**
 * Canonical structured location shape used throughout the app.
 *
 * `lat` / `lng` are stored only on the private teacher doc and used for
 * proximity ranking. They are stripped before syncing to the public profile.
 */
export interface NormalizedLocation {
  /** Full human-readable label, e.g. "Los Angeles, CA, United States" */
  formatted: string
  /** City / locality, e.g. "Los Angeles" */
  city: string
  /** Two-letter state/region code, e.g. "CA" */
  state: string
  /** Full state/region name, e.g. "California" */
  stateFull: string
  /** Two-letter country code, e.g. "US" */
  country: string
  /** Latitude (private — never written to public profile) */
  lat: number
  /** Longitude (private — never written to public profile) */
  lng: number
  /** Geohash for proximity queries. Precision 7 ≈ ~150m, but city-level safe. */
  geohash: string
  /** Provider used to source the location (for future migrations) */
  provider: 'mapbox' | 'manual'
}

/**
 * Public-safe location shape — what gets written to publicTeacherProfiles.
 * Note: no lat/lng. The geohash is kept because it is non-reversible at the
 * stored precision and is needed for future nearby-teacher queries.
 */
export type PublicLocation = Omit<NormalizedLocation, 'lat' | 'lng'>

/** Mapbox Geocoding API feature shape (subset we care about). */
interface MapboxFeature {
  id: string
  place_name: string
  text: string
  center: [number, number] // [lng, lat]
  context?: Array<{
    id: string
    short_code?: string
    text: string
  }>
  properties?: {
    short_code?: string
  }
}

/**
 * Compute a geohash for a (lat, lng) pair.
 * Returns null if either coordinate is invalid.
 */
export function computeGeohash(lat: number, lng: number): string {
  if (
    typeof lat !== 'number' ||
    typeof lng !== 'number' ||
    Number.isNaN(lat) ||
    Number.isNaN(lng)
  ) {
    return ''
  }
  try {
    return geohashForLocation([lat, lng])
  } catch {
    return ''
  }
}

/**
 * Parse a Mapbox Geocoding API feature into our NormalizedLocation shape.
 * Handles place / locality / region / country features uniformly.
 */
export function parseMapboxFeature(
  feature: MapboxFeature,
): NormalizedLocation | null {
  if (!feature?.center || feature.center.length !== 2) return null

  const [lng, lat] = feature.center
  const context = feature.context || []

  // Find region (state) and country in context array
  const regionCtx = context.find((c) => c.id?.startsWith('region'))
  const countryCtx = context.find((c) => c.id?.startsWith('country'))
  const placeCtx = context.find((c) => c.id?.startsWith('place'))

  // The feature itself might BE a place/region/country, so we fall back to it
  const featureType = feature.id?.split('.')[0] || ''

  let city = ''
  if (featureType === 'place' || featureType === 'locality') {
    city = feature.text || ''
  } else if (placeCtx) {
    city = placeCtx.text || ''
  }

  const stateFull =
    regionCtx?.text || (featureType === 'region' ? feature.text : '') || ''
  // short_code on region is like "US-CA" — extract the part after the dash
  const regionShortCode =
    regionCtx?.short_code ||
    (featureType === 'region' ? feature.properties?.short_code : '') ||
    ''
  const state = regionShortCode.includes('-')
    ? regionShortCode.split('-')[1].toUpperCase()
    : regionShortCode.toUpperCase()

  const country = (
    countryCtx?.short_code ||
    (featureType === 'country' ? feature.properties?.short_code : '') ||
    ''
  ).toUpperCase()

  return {
    formatted: feature.place_name || feature.text || '',
    city,
    state,
    stateFull,
    country,
    lat,
    lng,
    geohash: computeGeohash(lat, lng),
    provider: 'mapbox',
  }
}

/**
 * Strip private fields (lat, lng) from a NormalizedLocation before publishing.
 * The geohash is preserved for future proximity queries — it does not reveal
 * a precise address at city-level precision.
 */
export function extractPublicLocation(
  location: NormalizedLocation | null | undefined,
): PublicLocation | null {
  if (!location) return null
  const { lat: _lat, lng: _lng, ...publicFields } = location
  return publicFields
}

/**
 * Forward-geocode a query string against the Mapbox Geocoding API.
 * Returns up to `limit` city/region/country-level suggestions.
 *
 * Returns an empty array if no token is configured or the request fails.
 */
export async function geocodeQuery(
  query: string,
  options: {
    token?: string
    limit?: number
    signal?: AbortSignal
  } = {},
): Promise<NormalizedLocation[]> {
  const token = options.token || getMapboxToken()
  if (!token || !query.trim()) return []

  const limit = options.limit ?? 5
  const url =
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json` +
    `?access_token=${encodeURIComponent(token)}` +
    `&types=place,locality,region,country` +
    `&limit=${limit}` +
    `&autocomplete=true`

  try {
    const res = await fetch(url, { signal: options.signal })
    if (!res.ok) return []
    const data = await res.json()
    const features: MapboxFeature[] = data?.features || []
    return features
      .map(parseMapboxFeature)
      .filter((f): f is NormalizedLocation => f !== null)
  } catch (err: any) {
    // Aborted requests are expected during typing — don't log
    if (err?.name !== 'AbortError') {
      console.warn('[locationUtils] geocodeQuery failed:', err)
    }
    return []
  }
}

/**
 * Read the Mapbox token from Vite environment.
 * Returns empty string if not configured — the autocomplete will fall back
 * to a plain text input in that case.
 */
export function getMapboxToken(): string {
  try {
    // Vite exposes env vars on import.meta.env at build time
    const token = (import.meta as any)?.env?.VITE_MAPBOX_TOKEN
    return typeof token === 'string' ? token : ''
  } catch {
    return ''
  }
}

/** True if a Mapbox token is configured (autocomplete is available). */
export function isGeocodingAvailable(): boolean {
  return getMapboxToken().length > 0
}

/**
 * Build a legacy "City, State" string from a NormalizedLocation for backward
 * compatibility with any code that still reads the flat `city` / `state` fields.
 */
export function buildLegacyLocationString(
  location: NormalizedLocation | null | undefined,
): string {
  if (!location) return ''
  const parts = [location.city, location.state].filter(Boolean)
  return parts.join(', ')
}

/**
 * Build a NormalizedLocation from legacy free-text city/state values.
 * Used during the transition period — provider is marked 'manual' and there
 * are no coordinates, so it will not participate in proximity ranking until
 * the teacher re-enters their location through the autocomplete.
 */
export function buildManualLocation(
  city: string,
  state: string,
  country = '',
): NormalizedLocation | null {
  const trimmedCity = (city || '').trim()
  const trimmedState = (state || '').trim()
  if (!trimmedCity && !trimmedState) return null

  const formatted = [trimmedCity, trimmedState].filter(Boolean).join(', ')
  return {
    formatted,
    city: trimmedCity,
    state: trimmedState.length === 2 ? trimmedState.toUpperCase() : '',
    stateFull: trimmedState.length > 2 ? trimmedState : '',
    country: (country || '').toUpperCase(),
    lat: 0,
    lng: 0,
    geohash: '',
    provider: 'manual',
  }
}
